import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { FaStar, FaChevronLeft, FaChevronRight, FaPaw } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import Icon from './Icon';
import SectionTitle from './SectionTitle';
import { getReviews, ReviewsResponse } from '../services/reviewsService';

const AvaliacoesSection = styled.section`
  padding: 100px 0;
  background-color: #ffffff;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.05);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }

  .slick-slider {
    position: relative;
    margin-top: 2rem;
  }

  .slick-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    background: #ff9d5c !important;
    border-radius: 50%;
    display: flex !important;
    align-items: center;
    justify-content: center;
    color: white;
    z-index: 1;
    transition: all 0.3s ease;
    cursor: pointer;

    @media (max-width: 768px) {
      display: none !important;
    }

    &:hover {
      background: #e88c4a !important;
      transform: translateY(-50%) scale(1.1);
    }

    &.slick-disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &::before {
      display: none;
    }
  }

  .slick-prev {
    left: -60px;
  }

  .slick-next {
    right: -60px;
  }

  .slick-dots {
    bottom: -40px;

    li {
      button {
        &::before {
          font-size: 10px;
          color: #ff9d5c;
        }
      }

      &.slick-active {
        button {
          &::before {
            color: #ff9d5c;
          }
        }
      }
    }
  }
`;

const SectionDescription = styled.p`
  font-size: 1.1rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
  line-height: 1.6;
`;

const SlideWrapper = styled.div`
  height: 100%;
  padding: 15px;
  display: flex;

  @media (max-width: 768px) {
    padding: 10px 5px;
  }
`;

const Card = styled.div`
  background: #fff;
  padding: 2.5rem 2rem;
  border-radius: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  text-align: center;
  position: relative;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  height: 400px;
  width: 100%;
  display: flex !important;
  flex-direction: column;
  overflow: hidden;
  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 140px;
    background: linear-gradient(135deg, #ff9d5c15 0%, #ff853310 100%);
    border-radius: 30px 30px 100% 100%;
    transition: all 0.4s ease;
  }

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(255, 157, 92, 0.15);

    &::before {
      height: 160px;
      background: linear-gradient(135deg, #ff9d5c20 0%, #ff853315 100%);
    }
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  position: relative;
  z-index: 1;
`;

const TopSection = styled.div`
  position: relative;
  width: 100%;
  padding-top: 0.5rem;
  margin-bottom: 1rem;
`;

const MiddleSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0;
  justify-content: flex-start;
`;

const Photo = styled.div<{ src: string }>`
  width: 110px;
  height: 110px;
  border-radius: 50%;
  background-image: url(${props => props.src}), url('/images/default-avatar.svg');
  background-size: cover, contain;
  background-position: center;
  background-repeat: no-repeat;
  border: 4px solid #fff;
  box-shadow: 0 8px 25px rgba(255, 157, 92, 0.2);
  margin: 0 auto;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -8px;
    left: -8px;
    right: -8px;
    bottom: -8px;
    border-radius: 50%;
    border: 2px solid #ff9d5c20;
    transition: all 0.4s ease;
  }

  ${Card}:hover & {
    transform: scale(1.08);
    box-shadow: 0 10px 30px rgba(255, 157, 92, 0.25);

    &::after {
      transform: scale(1.1);
      border-color: #ff9d5c40;
    }
  }
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  color: #2c3e50;
  margin: 0.5rem 0 0.25rem;
  font-weight: 600;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  ${Card}:hover & {
    background: linear-gradient(135deg, #ff9d5c 0%, #ff8533 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const CardText = styled.p`
  color: #666;
  line-height: 1.6;
  font-style: italic;
  margin: 0;
  font-size: 0.95rem;
  position: relative;
  padding: 0 0.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.3s ease;
  
  &::before {
    content: '"';
    font-size: 2.5rem;
    position: absolute;
    top: -1.2rem;
    left: -0.5rem;
    color: #ff9d5c;
    opacity: 0.1;
    font-family: serif;
  }

  ${Card}:hover & {
    color: #444;
  }
`;

const Stars = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.3rem;
  color: #ff9d5c;
  margin: 0.4rem 0;
  transform: scale(1);
    transition: all 0.3s ease;

  ${Card}:hover & {
    transform: scale(1.05);
    gap: 0.4rem;
  }
`;

const CustomPrevArrow = (props: any) => {
  const { className, onClick } = props;
  return (
    <div className={className} onClick={onClick}>
      <Icon icon={FaChevronLeft} size={20} color="white" />
    </div>
  );
};

const CustomNextArrow = (props: any) => {
  const { className, onClick } = props;
  return (
    <div className={className} onClick={onClick}>
      <Icon icon={FaChevronRight} size={20} color="white" />
    </div>
  );
};

const settings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 5000,
  arrows: true,
  prevArrow: <CustomPrevArrow />,
  nextArrow: <CustomNextArrow />,
  responsive: [
    {
      breakpoint: 1200,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: false,
        arrows: false,
        dots: true,
      },
    },
  ],
};

const GoogleRatingSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.05);
  max-width: 400px;
  margin: 3rem auto;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #ff9d5c 0%, #ff8533 100%);
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
  }
`;

const GoogleIcon = styled.div`
  margin-right: 1.5rem;
  color: #4285F4;
`;

const RatingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const RatingText = styled.div`
  font-size: 1.8rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  span {
    font-size: 1rem;
    color: #666;
    font-weight: normal;
  }
`;

const GoogleStars = styled.div`
  display: flex;
  gap: 0.3rem;
  color: #ff9d5c;
`;

interface PawDecorationProps {
  $delay: string;
  $top: string;
  $left: string;
  $rotation: string;
}

const PawDecoration = styled.div<PawDecorationProps>`
  position: absolute;
  color: #ff9d5c;
  opacity: 0.1;
  transform: rotate(${(props) => props.$rotation});
  top: ${(props) => props.$top};
  left: ${(props) => props.$left};
  animation: float 3s ease-in-out ${(props) => props.$delay} infinite;
  z-index: 1;
`;

const Avaliacoes: React.FC = () => {
  const [reviewsData, setReviewsData] = useState<ReviewsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getProxiedImageUrl = (originalUrl: string) => {
    return `https://backend.clinicaestimapet.com.br/api/proxy-image?url=${encodeURIComponent(originalUrl)}`;
  };
  
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        
        const data = await getReviews();
        
        
        // Modificar as URLs das imagens para usar o proxy
        const reviewsWithProxiedImages = {
          ...data,
          reviews: data.reviews.map(review => ({
            ...review,
            profile_photo_url: getProxiedImageUrl(review.profile_photo_url)
          }))
        };
        
        setReviewsData(reviewsWithProxiedImages);
      } catch (err) {
        console.error('[DEBUG] Erro ao carregar avaliações do backend:', err);
        setError('Erro ao carregar avaliações');
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  if (loading) {
    return (
      <AvaliacoesSection id="avaliacoes">
        <Container>
          <SectionTitle>O Que Dizem Nossos Clientes</SectionTitle>
          <SectionDescription>Carregando avaliações...</SectionDescription>
        </Container>
      </AvaliacoesSection>
    );
  }

  if (error || !reviewsData) {
    return (
      <AvaliacoesSection id="avaliacoes">
        <Container>
          <SectionTitle>O Que Dizem Nossos Clientes</SectionTitle>
          <SectionDescription>Não foi possível carregar as avaliações no momento.</SectionDescription>
        </Container>
      </AvaliacoesSection>
    );
  }

  return (
    <AvaliacoesSection id="avaliacoes">
      <Container>
        <SectionTitle>O Que Dizem Nossos Clientes</SectionTitle>
        <SectionDescription>
          Confira os depoimentos de tutores que confiam seus pets aos nossos cuidados e compartilham suas experiências com a Estima Pet
        </SectionDescription>
        <GoogleRatingSection>
          <GoogleIcon>
            <Icon icon={FcGoogle} size={40} color="currentColor" />
          </GoogleIcon>
          <RatingContent>
            <RatingText>
              {reviewsData.rating} <span>Avaliação Google</span>
            </RatingText>
            <GoogleStars>
          {[...Array(5)].map((_, i) => (
                <Icon key={i} icon={FaStar} size={20} color="currentColor" />
              ))}
            </GoogleStars>
          </RatingContent>
        </GoogleRatingSection>
        <Slider {...settings}>
          {reviewsData.reviews.map((review, index) => (
            <SlideWrapper key={index}>
              <Card>
                <CardContent>
                  <TopSection>
                    <Photo 
                      src={review.profile_photo_url} 
                      onError={(e: any) => {
                        e.target.style.backgroundImage = "url('/images/default-avatar.png')";
                      }}
                    />
                    <Stars>
                      {[...Array(review.rating)].map((_, i) => (
                        <Icon key={i} icon={FaStar} size={26} color="currentColor" />
                      ))}
                    </Stars>
                  </TopSection>
                  <MiddleSection>
                    <CardTitle>{review.author_name}</CardTitle>
                    <CardText>
                      {review.text}
                    </CardText>
                  </MiddleSection>
                </CardContent>
              </Card>
            </SlideWrapper>
          ))}
        </Slider>
      </Container>

      {/* Patinhas decorativas */}
      <PawDecoration $delay="0s" $top="10%" $left="5%" $rotation="15deg">
        <Icon icon={FaPaw} size={40} color="currentColor" />
      </PawDecoration>
      <PawDecoration $delay="1s" $top="30%" $left="85%" $rotation="-10deg">
        <Icon icon={FaPaw} size={30} color="currentColor" />
      </PawDecoration>
      <PawDecoration $delay="2s" $top="60%" $left="15%" $rotation="25deg">
        <Icon icon={FaPaw} size={35} color="currentColor" />
      </PawDecoration>
      <PawDecoration $delay="1.5s" $top="80%" $left="75%" $rotation="-20deg">
        <Icon icon={FaPaw} size={25} color="currentColor" />
      </PawDecoration>
    </AvaliacoesSection>
  );
};

export default Avaliacoes; 